import { apiUrl } from "../constants/const";
import logger from "../services/Logger";
import { v4 as uuidv4 } from "uuid";
import { 
  updateRegenerateMessage,
  updateMessageModelId,
  updateAnswerUsage,
  runMessageStream,
  addGenerateErrorMessage,
} from "../utils/streamingUtils";
import { generateMessageApi } from "./generate/messageOperations";
import { createMessageLoadingHandlers } from "./generate/useMessageLoading";
import useStreamingStateStore from "../store/useStreamingStateStore";
import PromptStore, { resolveSelectedAnswerId } from "../store/promptStore";

const regenerate = async ({
  id,
  index,
  messages,
  regenerateMessageMutation,
  setIsLatestMessageLoading,
  modeRef,
  chat_id,
  orgId,
  user,
  files,
  modelSelected,
  setRegenerateIndex,
  setGenerateStatus,
  space,
  chat,
  onApprovalCard,
  queryClient,
  deviceId = null,
  styleSelection = null,
  styleVersionId = null,
}) => {
  // Initialize loading state handlers
  const loadingHandlers = createMessageLoadingHandlers({
    setIsLatestMessageLoading,
    setGenerateStatus,
    setRegenerateIndex,
    chat_id,
  });

  // Snapshot activeSkills synchronously before any awaits
  const activeSkills = PromptStore.getState().activeSkills || [];

  try {
    // Find the message to regenerate
    let message_data = null;
    for (const message of messages) {
      if (message.id === id) {
        message_data = message;
        break;
      }
    }

    const selectedAnswerId = PromptStore.getState().selectedAnswerByPrompt[id];
    const sourceAnswerId = resolveSelectedAnswerId(message_data?.answers, selectedAnswerId);
    const sourceAnswer = message_data?.answers?.find((answer) => answer.id === sourceAnswerId);
    if (!message_data || !sourceAnswerId) {
      throw new Error("The selected response is no longer available for regeneration.");
    }

    const prompt_id = uuidv4();
    const answer_id = uuidv4();
    const turn_id = message_data.turn_id || message_data.id;
    const source_prompt_id = sourceAnswer?.prompt_id || message_data.id;
    
    // Create optimistic answer in cache first so the old content/activity disappears
    // in the same render that the beaming loader appears — prevents a one-render flash
    // where the loader overlaps the still-visible previous answer.
    if (regenerateMessageMutation) {
      await regenerateMessageMutation.mutateAsync({
        messageId: id,
        promptId: prompt_id,
        answerId: answer_id,
        modelId: modelSelected,
      });
    }
    PromptStore.getState().setSelectedAnswer(id, answer_id);

    // Start loading state after optimistic update is in the cache.
    // Also clear the streaming store immediately so the completed activity snapshot
    // from the previous answer doesn't stay visible during network latency.
    loadingHandlers.startLoading(id, index, answer_id);
    useStreamingStateStore.getState().startStreaming(id);

    // Use unified API
    const reader = await generateMessageApi({
      operation: 'regenerate',
      prompt: message_data.content,
      chatId: chat_id,
      promptId: prompt_id,
      answerId: answer_id,
      turnId: turn_id,
      sourcePromptId: source_prompt_id,
      sourceAnswerId,
      apiUrl,
      space,
      chat,
      user,
      orgId,
      modeRef,
      modelId: modelSelected,
      modelOverride: Boolean(modelSelected),
      assetIds: message_data.asset_ids || [],
      messages,
      activeSkills,
      deviceId,
      styleSelection,
      styleVersionId,
    });

    // Use shared message-stream wiring.
    await runMessageStream({
      reader,
      messageId: id,
      loadingHandlers,
      writeChunk: (content) =>
        updateRegenerateMessage(queryClient, chat_id, space?.space_id, id, answer_id, content),
      writeModel: (modelId) =>
        updateMessageModelId(queryClient, chat_id, space?.space_id, id, modelId),
      onUsage: (data) =>
        updateAnswerUsage(queryClient, chat_id, space?.space_id, id, data.answer_id, data),
      onApprovalCard,
    });
  } catch (error) {
    const handled = loadingHandlers.handleError(error);
    if (!handled) {
      logger.error('regenerate', 'Error fetching messages', { error: error.message, chat_id, messageId: id });
      addGenerateErrorMessage(
        queryClient,
        chat_id,
        space?.space_id,
        id,
        "\n" + (error.message || "Something went wrong while generating the response. Please try again.")
      );
    }
  }
};

export default regenerate;